import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Loader2, ArrowRight, KeyRound, Lock, Eye, EyeOff } from 'lucide-react';
import { Toast } from '../../../components/Toast';
import { validationRules } from '../../../utils/validation';
import { authService } from '../../../services/authService';

type Step = 'email' | 'verify' | 'reset';

interface EmailFormData {
  email: string;
}

interface VerifyFormData {
  otp: string;
}

interface ResetFormData {
  newPassword: string;
  confirmPassword: string;
}

export const ForgotPasswordForm = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);
  const [showToast, setShowToast] = useState(false);

  const emailForm = useForm<EmailFormData>();
  const verifyForm = useForm<VerifyFormData>();
  const resetForm = useForm<ResetFormData>();

  // Countdown timer effect
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && !canResend) {
      setCanResend(true);
    }
  }, [countdown, canResend]);

  // Step 1: Request OTP
  const onSubmitEmail = async (data: EmailFormData) => {
    try {
      setIsLoading(true);
      setApiError(null);

      const response = await authService.forgotPassword(data.email);
      setEmail(data.email);
      setCountdown(60); // 60 second countdown
      setCanResend(false);
      
      // Extract attempts remaining from response if available
      if (response?.data?.data?.attemptsRemaining !== undefined) {
        setAttemptsRemaining(response.data.data.attemptsRemaining);
      }
      
      setStep('verify');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 429) {
          setApiError('Too many attempts. Please wait 1 hour before trying again.');
        } else {
          setApiError(error.response?.data?.message || 'Failed to send verification code');
        }
      } else {
        setApiError('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (!canResend || !email) return;
    
    try {
      setIsLoading(true);
      setApiError(null);

      const response = await authService.forgotPassword(email);
      setCountdown(60);
      setCanResend(false);
      
      if (response?.data?.data?.attemptsRemaining !== undefined) {
        setAttemptsRemaining(response.data.data.attemptsRemaining);
      }
      
      // Show success message
      setApiError(null);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 429) {
          setApiError('Too many attempts. Account locked for 1 hour.');
        } else {
          setApiError(error.response?.data?.message || 'Failed to resend code');
        }
      } else {
        setApiError('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP
  const onSubmitVerify = async (data: VerifyFormData) => {
    try {
      setIsLoading(true);
      setApiError(null);

      await authService.verifyResetCode(email, data.otp);
      setOtp(data.otp);
      setStep('reset');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setApiError(error.response?.data?.message || 'Invalid verification code');
      } else {
        setApiError('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Reset Password
  const onSubmitReset = async (data: ResetFormData) => {
    try {
      setIsLoading(true);
      setApiError(null);

      await authService.resetPassword(email, otp, data.newPassword);
      
      // Show success toast
      setShowToast(true);
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setApiError(error.response?.data?.message || 'Failed to reset password');
      } else {
        setApiError('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Render Email Step
  if (step === 'email') {
    return (
      <>
        {showToast && (
          <Toast
            type="success"
            title="Password Reset Successful!"
            description="Your password has been reset. Redirecting to login..."
            onClose={() => setShowToast(false)}
          />
        )}
        <form onSubmit={emailForm.handleSubmit(onSubmitEmail)} className="space-y-6">
        {apiError && (
          <div className="p-3 bg-red-900 bg-opacity-20 border border-red-500 rounded-lg text-red-400 text-sm">
            {apiError}
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
            EMAIL ADDRESS
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
            <input
              id="email"
              type="email"
              {...emailForm.register('email', validationRules.email)}
              className={`w-full pl-10 pr-4 py-3 bg-gray-800 border ${
                emailForm.formState.errors.email ? 'border-red-500' : 'border-gray-700'
              } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent transition-all`}
              style={{ '--tw-ring-color': '#23F47C' } as React.CSSProperties}
              placeholder="admin@gymnet.com"
              autoComplete="email"
            />
          </div>
          {emailForm.formState.errors.email && (
            <p className="mt-1 text-sm text-red-400">
              {emailForm.formState.errors.email.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 px-4 font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-black"
          style={{ backgroundColor: '#23F47C' }}
          onMouseEnter={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#1de06d')}
          onMouseLeave={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#23F47C')}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Sending Code...</span>
            </>
          ) : (
            <>
              <span>Send Verification Code</span>
              <ArrowRight className="h-5 w-5" />
            </>
          )}
        </button>
      </form>
      </>
    );
  }

  // Render Verify OTP Step
  if (step === 'verify') {
    return (
      <>
        {showToast && (
          <Toast
            type="success"
            title="Password Reset Successful!"
            description="Your password has been reset. Redirecting to login..."
            onClose={() => setShowToast(false)}
          />
        )}
        <form onSubmit={verifyForm.handleSubmit(onSubmitVerify)} className="space-y-6">
        {apiError && (
          <div className="p-3 bg-red-900 bg-opacity-20 border border-red-500 rounded-lg text-red-400 text-sm">
            {apiError}
          </div>
        )}

        <div className="text-center mb-4">
          <p className="text-gray-400 text-sm">
            We've sent a verification code to <span className="text-white font-medium">{email}</span>
          </p>
          {attemptsRemaining < 5 && (
            <p className="text-yellow-400 text-xs mt-2">
              {attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining
            </p>
          )}
        </div>

        <div>
          <label htmlFor="otp" className="block text-sm font-medium text-gray-300 mb-2">
            VERIFICATION CODE
          </label>
          <div className="relative">
            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
            <input
              id="otp"
              type="text"
              {...verifyForm.register('otp', {
                required: 'Verification code is required',
                pattern: {
                  value: /^\d{6}$/,
                  message: 'Code must be 6 digits'
                }
              })}
              className={`w-full pl-10 pr-4 py-3 bg-gray-800 border ${
                verifyForm.formState.errors.otp ? 'border-red-500' : 'border-gray-700'
              } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent transition-all text-center text-2xl tracking-widest`}
              style={{ '--tw-ring-color': '#23F47C' } as React.CSSProperties}
              placeholder="000000"
              maxLength={6}
            />
          </div>
          {verifyForm.formState.errors.otp && (
            <p className="mt-1 text-sm text-red-400">
              {verifyForm.formState.errors.otp.message}
            </p>
          )}
        </div>

        {/* Countdown and Resend */}
        <div className="text-center">
          {countdown > 0 ? (
            <p className="text-sm text-gray-400">
              Resend code in <span className="text-white font-medium">{countdown}s</span>
            </p>
          ) : (
            <button
              type="button"
              onClick={handleResendOTP}
              disabled={!canResend || isLoading}
              className="text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: canResend ? '#23F47C' : '#9ca3af' }}
              onMouseEnter={(e) => canResend && !isLoading && (e.currentTarget.style.color = '#1de06d')}
              onMouseLeave={(e) => canResend && !isLoading && (e.currentTarget.style.color = '#23F47C')}
            >
              Didn't receive the code? Resend
            </button>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 px-4 font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-black"
          style={{ backgroundColor: '#23F47C' }}
          onMouseEnter={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#1de06d')}
          onMouseLeave={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#23F47C')}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Verifying...</span>
            </>
          ) : (
            <>
              <span>Verify Code</span>
              <ArrowRight className="h-5 w-5" />
            </>
          )}
        </button>

        <button
          type="button"
          onClick={() => setStep('email')}
          className="w-full text-sm text-gray-400 hover:text-white transition-colors"
        >
          Use a different email
        </button>
      </form>
      </>
    );
  }

  // Render Reset Password Step
  if (step === 'reset') {
    return (
      <>
        {showToast && (
          <Toast
            type="success"
            title="Password Reset Successful!"
            description="Your password has been reset. Redirecting to login..."
            onClose={() => setShowToast(false)}
          />
        )}
        <form onSubmit={resetForm.handleSubmit(onSubmitReset)} className="space-y-6">
        {apiError && (
          <div className="p-3 bg-red-900 bg-opacity-20 border border-red-500 rounded-lg text-red-400 text-sm">
            {apiError}
          </div>
        )}

        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-2">
            NEW PASSWORD
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
            <input
              id="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              {...resetForm.register('newPassword', validationRules.password)}
              className={`w-full pl-10 pr-12 py-3 bg-gray-800 border ${
                resetForm.formState.errors.newPassword ? 'border-red-500' : 'border-gray-700'
              } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent transition-all`}
              style={{ '--tw-ring-color': '#23F47C' } as React.CSSProperties}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
              aria-label={showNewPassword ? 'Hide password' : 'Show password'}
            >
              {showNewPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
          {resetForm.formState.errors.newPassword && (
            <p className="mt-1 text-sm text-red-400">
              {resetForm.formState.errors.newPassword.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
            CONFIRM PASSWORD
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              {...resetForm.register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) => {
                  const newPassword = resetForm.getValues('newPassword');
                  return value === newPassword || 'Passwords do not match';
                }
              })}
              className={`w-full pl-10 pr-12 py-3 bg-gray-800 border ${
                resetForm.formState.errors.confirmPassword ? 'border-red-500' : 'border-gray-700'
              } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent transition-all`}
              style={{ '--tw-ring-color': '#23F47C' } as React.CSSProperties}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
          {resetForm.formState.errors.confirmPassword && (
            <p className="mt-1 text-sm text-red-400">
              {resetForm.formState.errors.confirmPassword.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 px-4 font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-black"
          style={{ backgroundColor: '#23F47C' }}
          onMouseEnter={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#1de06d')}
          onMouseLeave={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#23F47C')}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Resetting Password...</span>
            </>
          ) : (
            <>
              <span>Reset Password</span>
              <ArrowRight className="h-5 w-5" />
            </>
          )}
        </button>
      </form>
      </>
    );
  }

  // If somehow we reach here, return null
  return null;
};
